import { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Barcode from 'react-barcode';

import { Expediente, consultarExpediente } from '../config/index';
import { formatearFechaLocal, formatearFechaHistorial } from '../utils/dateUtils';
import {
  Box,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

export const ConsultaExpediente = () => {
  const location = useLocation();
  const initialData = location.state as { expediente?: string; email?: string } || {};
  
  const [expediente, setExpediente] = useState(initialData.expediente || '');
  const [email, setEmail] = useState(initialData.email || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Expediente | null>(null);
  const [buttonKey, setButtonKey] = useState(0);
  
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);
    
    // Usar requestAnimationFrame para asegurar que el DOM se actualiza correctamente
    requestAnimationFrame(() => {
      setLoading(true);
      setButtonKey(prev => prev + 1);
    });

    try {
      const expedienteResult = await consultarExpediente(expediente);
      
      if (!expedienteResult) {
        setError('No se encontró el expediente o los datos no coinciden');
        // Pequeño delay para evitar problemas de sincronización del DOM
        setTimeout(() => {
          setLoading(false);
          setButtonKey(prev => prev + 1);
        }, 100);
        return;
      }
      
      // Verificar que el email coincida con el del expediente
      if (expedienteResult.email !== email) {
        setError('No se encontró el expediente o los datos no coinciden');
        setTimeout(() => {
          setLoading(false);
          setButtonKey(prev => prev + 1);
        }, 100);
        return;
      }
      
      setResult(expedienteResult);
      setTimeout(() => {
        setLoading(false);
        setButtonKey(prev => prev + 1);
      }, 100);
    } catch (error: unknown) {
      console.error('Error al consultar expediente:', error);
      setError('No se encontró el expediente o los datos no coinciden');
      setTimeout(() => {
        setLoading(false);
        setButtonKey(prev => prev + 1);
      }, 100);
    }
  }, [expediente, email]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const consultarInicial = async () => {
      if (initialData.expediente && initialData.email && isMounted) {
        // Esperar un poco para que el componente esté completamente montado
        timeoutId = setTimeout(async () => {
          if (isMounted) {
            const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
            await handleSubmit(fakeEvent);
          }
        }, 300);
      }
    };

    consultarInicial();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [initialData.expediente, initialData.email, handleSubmit]);

  return (
    <Box
      minH="100vh"
      py={10}
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.900"
      />
      <Container maxW="container.sm" position="relative" zIndex={1}>
        <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                as="h1"
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Consulta tu Expediente
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <Text 
            color="gray.700"
            textAlign="center"
            fontWeight="semibold"
            bg="whiteAlpha.600"
            py={2}
            px={4}
            borderRadius="lg"
            backdropFilter="blur(10px)"
          >
            Introduce el número de expediente y el email con el que realizaste la denuncia
          </Text>

          <Box 
            as="form" 
            onSubmit={handleSubmit}
            bg="white"
            p={8}
            borderRadius="xl"
            boxShadow="xl"
          >
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>Número de Expediente</FormLabel>
                <Input
                  value={expediente}
                  onChange={(e) => setExpediente(e.target.value)}
                  placeholder="Ej: ODAT1234567890ABC"
                  pattern="ODAT[0-9]{10}[A-Z]{3}"
                  title="El formato debe ser ODAT + 10 dígitos + 3 letras mayúsculas"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                />
              </FormControl>

              <Button
                key={`submit-button-${buttonKey}`}
                type="submit"
                colorScheme="red"
                size="lg"
                width="full"
                isLoading={loading}
                loadingText="Consultando..."
                disabled={loading}
              >
                Consultar
              </Button>
            </VStack>
          </Box>

          {error && (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Box bg="gray.50" p={6} borderRadius="lg">
              <VStack spacing={6} align="stretch">
                <Box display="flex" justifyContent="center" p={4} bg="white" borderRadius="md">
                  <Barcode 
                    key={result.id}
                    value={result.id}
                    width={1}
                    height={50}
                    displayValue={true}
                  />
                </Box>
                <Alert 
                  status={
                    result.estado === 'Resuelto' ? 'success' : 
                    result.estado === 'En trámite' ? 'warning' : 
                    'info'
                  }
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle>
                      Estado: <Text as="span" 
                        color={
                          result.estado === 'Resuelto' ? 'green.600' : 
                          result.estado === 'En trámite' ? 'orange.600' : 
                          'gray.600'
                        }
                        fontWeight="bold"
                      >
                        {result.estado}
                      </Text>
                    </AlertTitle>
                    <AlertDescription>
                      Fecha de creación: {formatearFechaLocal(result.fechaCreacion)}
                    </AlertDescription>
                  </Box>
                </Alert>

                <Box>
                  <Text fontWeight="bold">Tipo de Barrera:</Text>
                  <Text>{result.tipoBarrera}</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Descripción:</Text>
                  <Text>{result.descripcion}</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Departamento Asignado:</Text>
                  <Text>{result.Departamento}</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold">Evolución del Expediente:</Text>
                  {result.historial && result.historial.length > 0 ? (
                    <VStack spacing={4} mt={2} align="stretch">
                      {result.historial.map((cambio, index) => (
                        <Box 
                          key={`${result.id}-historial-${index}-${cambio.fecha}`} 
                          p={4} 
                          bg="white" 
                          borderRadius="md" 
                          shadow="sm"
                        >
                          <Text fontSize="sm" color="gray.600" fontWeight="medium">
                            {formatearFechaHistorial(cambio.fecha)}
                          </Text>
                          <Text fontWeight="medium" mt={1}>{cambio.cambio}</Text>
                          {cambio.informacionDenunciante && (
                            <Text mt={2} color="blue.600" fontSize="sm">
                              {cambio.informacionDenunciante}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Text color="gray.500">No hay evolución registrada</Text>
                  )}
                </Box>
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};
